<template>
  <div>
    <div v-if="loading || !formLoaded">
      <span class="spinner"></span>
    </div>
    <div v-if="!loading && formLoaded">
      <b-form
        @submit.stop.prevent="onClickSave"
        :class="{
          'p-3 box-shadow2 rounded': !isDarkMode,
          'dark-div p-3 box-shadow2 rounded': isDarkMode
        }"
      >
        <b-row>
          <b-col>
            <b-form-group id="group-licenseName" label-for="input-licenseName">
              <template v-slot:label>
                License Name
                <span class="text-danger">*</span>
              </template>
              <multiselect
                id="group-licenseName"
                v-model="form.licenseName"
                ref="multiselect"
                placeholder="Select
              License Name"
                label="text"
                :options="licenseOptions"
                :class="{ 'box-shadow3': !isDarkMode, darkMode: isDarkMode }"
              ></multiselect>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12" lg="6">
            <template> Select Date </template>
            <b-row>
              <b-col sm="12" lg="6">
                <b-input-group class="box-shadow2 mb-2">
                  <b-input-group-append>
                    <b-form-datepicker
                      button-only
                      id="start-datepicker"
                      placeholder="Start Date"
                      :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                      v-model="form.startDate"
                      class="box-shadow3"
                    ></b-form-datepicker>
                  </b-input-group-append>
                  <b-form-input
                    id="example-input"
                    v-model="form.startDate"
                    type="text"
                    placeholder="Issue Date"
                    autocomplete="off"
                    :class="{
                      '': !isDarkMode,
                      'bg-dark text-light placeholder-light': isDarkMode
                    }"
                    disabled
                  ></b-form-input>
                </b-input-group>
              </b-col>
              <b-col>
                <b-input-group class="box-shadow2 mb-2">
                  <b-input-group-append>
                    <b-form-datepicker
                      button-only
                      id="end-datepicker"
                      placeholder="End Date"
                      :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                      v-model="form.endDate"
                      class="box-shadow3"
                    ></b-form-datepicker>
                  </b-input-group-append>
                  <b-form-input
                    id="example-input"
                    v-model="form.endDate"
                    type="text"
                    placeholder="Expiry Date"
                    autocomplete="off"
                    :class="{
                      '': !isDarkMode,
                      'bg-dark text-light placeholder-light': isDarkMode
                    }"
                    disabled
                  ></b-form-input>
                </b-input-group>
              </b-col>
            </b-row>
            <b-form-text style="margin-bottom: 8px" id="input-role-help"
              >Note: Please Don't fill the date if license does not have any date</b-form-text
            >
          </b-col>
          <b-col style="display: flex; align-items: center">
            <b-form-group v-if="this.formType === 'new'" id="group-products" label-for="input-products">
              <template v-slot:label>
                Upload License
                <span class="text-danger">*</span>
              </template>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,image/*,application/pdf"
                @change="uploadFile($event)"
              />
            </b-form-group>
            <a v-else :href="form.file" target="_blank" rel="noopener noreferrer">
              {{ form.file }}
            </a>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-group id="group-products" label-for="input-products">
              <template v-slot:label>
                Store Id
                <span class="text-danger">*</span>
              </template>
              <multiselect
                v-model="storeId"
                ref="multiselect"
                tag-placeholder="Add this as new tag"
                placeholder="Select Store Id"
                label="text"
                track-by="value"
                :options="stores"
                :class="{
                  'box-shadow3': !isDarkMode,
                  darkMode: isDarkMode
                }"
              ></multiselect>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group>
          <b-row class="mt-2">
            <b-col class="text-right">
              <b-button
                class="box-shadow2"
                type="submit"
                size="sm"
                :variant="isDarkMode ? 'outline-success' : 'success'"
                :disabled="loading || uploadingFile"
              >
                <span class="spinner spinner-white" v-if="loading || uploadingFile"></span>
                <font-awesome-icon :icon="['fas', 'save']" class="mr-1" v-if="!loading && !uploadingFile" />
                {{ uploadingFile ? 'Uploading...' : this.formType === 'new' ? 'Save' : 'Update' }}
              </b-button>
              <b-button
                class="ml-2 box-shadow2"
                size="sm"
                :variant="isDarkMode ? 'outline-warning' : 'warning'"
                :to="{ path: `/license-upload` }"
              >
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-left']" class="mr-1" />Back to list
              </b-button>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import moment from 'moment';
import { mapGetters, mapState, mapActions } from 'vuex';
import configService from '@/services/configService';
import Multiselect from 'vue-multiselect';

export default {
  name: 'LicenseFormBox',
  components: {
    Multiselect
  },
  props: {
    listUrl: String,
    formType: String,
    licenseType: String
  },
  metaInfo() {
    return {
      meta: [
        {
          name: 'description',
          content: this.metaDescription
        }
      ]
    };
  },
  data() {
    return {
      licenseName: '',
      timeStamp: '',
      storeId: '',
      stores: [],
      licenseOptions: [
        { text: 'Shop and Establishment License', value: 'Shop and Establishment License' },
        { text: 'Trade License', value: 'Trade License' },
        { text: 'Abstract Documents', value: 'Abstract Documents' },
        { text: 'Minimum Wages Act Certificate', value: 'Minimum Wages Act Certificate' },
        { text: 'Gratuity Act Certificate', value: 'Gratuity Act Certificate' },
        { text: 'Maternity Act Certificate', value: 'Maternity Act Certificate' },
        { text: 'ESIC Certificate', value: 'ESIC Certificate' },
        { text: 'GST Registration', value: 'GST Registration' },
        { text: 'FoSTaC', value: 'FoSTaC' },
        { text: 'Medical', value: 'Medical' },
        { text: 'FSSAI License (For Food-Related Businesses)', value: 'FSSAI License (For Food-Related Businesses)' },
        {
          text: 'Weighing Machine License and Weight and Measurement License',
          value: 'Weighing Machine License and Weight and Measurement License'
        },
        { text: 'Fire NOC (No Objection Certificate)', value: 'Fire NOC (No Objection Certificate)' },
        { text: 'Insecticide License', value: 'Insecticide License' },
        { text: 'Liquor License', value: 'Liquor License' },
        { text: 'Labour License', value: 'Labour License' },
        { text: 'Contract Labour Registration', value: 'Contract Labour Registration' },
        { text: 'Weighing Machine License', value: 'Weighing Machine License' },
        { text: 'Police License', value: 'Police License' },
        { text: 'Drug Licence', value: 'Drug Licence' },
        { text: 'APMC', value: 'APMC' },
        { text: 'Others License', value: 'Others License' }
      ],
      file: null,
      selectedFile: null,
      formLoaded: true,
      form: {
        licenseName: null,
        startDate: null,
        endDate: null,
        file: '',
        storeId: ''
      },
      dateTimeFormat: configService.get('format').pickerDateTime,
      minDate: moment().format('YYYY-MM-DD')
    };
  },
  validations() {
    const formValidation = {
      licenseName: {
        required
      }
    };

    if (this.formType === 'new') {
      formValidation.licenseName.required = required;
    }

    return { form: formValidation };
  },
  mounted() {
    this.getStoreByUser();
    this.$nextTick(async () => {
      if (this.formType === 'new') {
        this.formLoaded = true;
        this.$v.$touch(); // Set initial validation
        this.$v.$reset(); // Reset $dirty
      }
    });
  },
  computed: {
    metaDescription() {
      return this.formType === 'new' ? 'Add new License' : 'Update License';
    },
    ...mapGetters('alert', ['errorMessages']),
    ...mapState('license', ['loading', 'license', 'uploadingFile']),
    ...mapState('cstSubmission', ['store']),
    ...mapState('darkMode', ['isDarkMode'])
  },
  methods: {
    ...mapActions('license', ['postOne', 'uploadDocument']),
    ...mapActions('cstSubmission', ['getStoreByUser']),

    uploadFile(event) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        const fileType = selectedFile.type;
        const fileName = selectedFile.name.toLowerCase();

        const isValidPdf = fileType === 'application/pdf' || fileName.endsWith('.pdf');

        const isValidImage =
          fileType.startsWith('image/') ||
          fileName.endsWith('.jpg') ||
          fileName.endsWith('.jpeg') ||
          fileName.endsWith('.png') ||
          fileName.endsWith('.gif') ||
          fileName.endsWith('.bmp') ||
          fileName.endsWith('.webp');

        if (!isValidPdf && !isValidImage) {
          this.$swal({
            title: 'Invalid File',
            text: 'Only PDF files and images (JPG, JPEG, PNG, GIF, BMP, WEBP) are allowed.',
            icon: 'error'
          });
          event.target.value = '';
          this.selectedFile = null;
          return;
        }

        // Just store the file, don't upload yet
        this.selectedFile = selectedFile;
        this.timeStamp = new Date().getTime();
      }
    },
    onClickSave() {
      if (this.form.startDate) {
        this.$swal({
          title: 'Are you sure you want to save the license with the following details?',
          html: `<strong>Issue Date:</strong> ${this.form.startDate || 'Not specified'}<br>
             <strong>Expiry Date:</strong> ${this.form.endDate || 'Not specified'}`,
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Save',
          cancelButtonText: 'Recheck',
          background: this.isDarkMode ? '#464d54' : '#ffffff',
          customClass: {
            title: this.isDarkMode ? 'text-light' : 'text-dark',
            content: this.isDarkMode ? 'text-light' : 'text-dark'
          },
          preConfirm: () => {
            this.onSubmit();
          }
        });
      } else {
        this.onSubmit();
      }
    },
    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      }
      if (this.formType === 'new' && !this.selectedFile) {
        this.$swal({
          title: 'Error',
          text: 'Please select a license file before submitting.',
          icon: 'error'
        });
        return false;
      }

      if (this.formType === 'new' && this.selectedFile) {
        await this.uploadDocument({
          file: this.selectedFile,
          fileName: `${this.storeId.value}-${this.form.licenseName?.value}-${this.timeStamp}-${this.selectedFile.name}`
        });
      }
      const license = {
        uploadedFileName:
          this.formType === 'new'
            ? `${this.storeId.value}-${this.form.licenseName?.value}-${this.timeStamp}-${this.selectedFile.name}`
            : this.form.licenseName?.value,
        active: true,
        fileName: this.form.licenseName?.value,
        startDate: this.form.startDate || '2000-01-01',
        endDate: this.form.endDate || '2099-01-01',
        storeid: this.storeId.value,
        ...(this.formType !== 'new' && {
          fileUrl: this.form.file
        }),
        ...(this.formType === 'new' && {
          isEmailSent30Days: false,
          isEmailSent45Days: false
        })
      };
      if (this.formType === 'new') {
        this.$emit('add', { license });
      } else {
        this.$emit('edit', { license });
      }
      return false;
    }
  },
  watch: {
    license(newValue) {
      if (!this.license.id) {
        return;
      }
      this.form.licenseName = { text: newValue.fileName, value: newValue.fileName };
      this.form.startDate = newValue.startDate ? moment(newValue.startDate).format('YYYY-MM-DD') : null;
      this.form.endDate = newValue.endDate ? moment(newValue.endDate).format('YYYY-MM-DD') : null;
      this.storeId = { text: newValue.storeid, value: newValue.storeid };
      this.form.file = newValue.fileUrl;

      //      this.formLoaded = true;
      //      this.$v.$touch(); // Set initial validation
      //      this.$v.$reset(); // Reset $dirty
    },
    store(newValue) {
      this.stores = newValue.map(store => ({ text: store.storeid, value: store.storeid }));
    }
  }
};
</script>
<style src="vue-search-select/dist/VueSearchSelect.css"></style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.calender {
  display: flex;
  width: auto;
  margin-left: -9px;
  margin-bottom: 0.7rem;
  margin-top: 4%;
}

.date {
  margin-left: -10px;
}

.upload {
  margin-top: 8px;
}

.videoPlayer {
  width: 25%;
}
</style>
